import { API_CONFIG } from '../config';
import type { Vehicle } from '../../types/booking';

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'active'].includes(normalized)) return true;
    if (['false', '0', 'no', 'inactive'].includes(normalized)) return false;
  }
  return undefined;
};

export const bookingService = {
  fetchVehicles: async (category?: string): Promise<Vehicle[]> => {
    try {
      const response = await fetch(
        `${API_CONFIG.baseUrl}/api/admin/fleets/`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch vehicles');
      }

      const result = await response.json();

      const rawList = Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.results)
          ? result.results
          : Array.isArray(result)
            ? result
            : [];

      const vehicles: Vehicle[] = rawList.map((fleet: any) => ({
        id: String(fleet.id),
        category: fleet.category || '',
        brand: fleet.brand || '',
        name: fleet.name,
        image: fleet.image || '',
        passengers: fleet.passengers ?? fleet.capacity ?? 0,
        luggage: fleet.suitcases ?? fleet.max_suitcases ?? 0,
        cabinBags: fleet.cabinBags ?? fleet.max_cabin_bags ?? 0,

        // Base / standard vehicle price
        price: Number(
          fleet.basePrice ??
            fleet.base_price ??
            0
        ),

        basePrice: Number(
          fleet.basePrice ??
            fleet.base_price ??
            0
        ),

        // Day Trip vehicle price
        dayTripPrice: Number(
          fleet.dayTripPrice ??
            fleet.day_trip_price ??
            0
        ),

        // Hourly vehicle price
        hourlyPrice: Number(
          fleet.hourlyPrice ??
            fleet.hourly_price ??
            0
        ),

        // Distance-based vehicle price
        pricePerKm: Number(
          fleet.pricePerKm ??
            fleet.price_per_km ??
            0
        ),

        // Minimum advance notice required before pickup
        minimumBookingHours: Number(
          fleet.minimumBookingHours ??
            fleet.minimum_booking_hours ??
            1
        ),

        description: fleet.description || '',
        features: fleet.features || fleet.tags || [],

        active:
          parseBoolean(
            fleet.active ??
              fleet.is_active
          ) ?? true,

        showOnHomepage:
          parseBoolean(
            fleet.showOnHomepage ??
              fleet.show_on_homepage
          ) ?? true,
      }));

      if (category) {
        return vehicles.filter(
          (v) =>
            v.category.toLowerCase() ===
            category.toLowerCase()
        );
      }

      const homepageVehicles = vehicles.filter(
        (vehicle) =>
          vehicle.active !== false &&
          vehicle.showOnHomepage !== false
      );

      return homepageVehicles.length > 0
        ? homepageVehicles
        : vehicles;
    } catch (error) {
      console.error('Error fetching fleet vehicles:', error);
      return [];
    }
  },

  submitBooking: async (
    payload: any
  ): Promise<{
    success: boolean;
    message: string;
    bookingId?: string;
  }> => {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/api/user/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to submit booking');
      }

      const data = await response.json();

      return {
        success: true,
        message: 'Booking confirmed',
        bookingId: data.bookingId,
      };
    } catch (error) {
      console.error('Error submitting booking to backend:', error);

      return {
        success: false,
        message:
          'An error occurred while submitting your booking. Please try again.',
      };
    }
  },
};